import * as React from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

import { SIDE_MENU_ROW_HEIGHT_PX } from "./side-menu-item.component";

export type SideMenuSectionTitle = {
  /** "Administration", "Leads": the area this menu belongs to. */
  label: string;
  Icon: React.ElementType;
  /** Suno-staff areas (Administration) render in the amber staff accent. */
  staff?: boolean;
};

type SideMenuSectionTitleProps = SideMenuSectionTitle & {
  collapsed: boolean;
};

/**
 * Names the area a sub-section menu belongs to, pinned under the header above
 * the rows (ENG-2535 follow-up): the same icon and accent as the main menu's row
 * that led here, so Administration reads as Administration at a glance. Icon
 * only in the rail; the label returns with the names.
 */
export default function SideMenuSectionTitleRow({
  label,
  Icon,
  staff = false,
  collapsed
}: SideMenuSectionTitleProps) {
  const theme = useTheme();
  const accent = staff
    ? theme.palette.sideMenu.staffAccent
    : theme.palette.sideMenu.itemBgcolorSelected;

  return (
    <Box
      component="h2"
      data-testid="side-menu-section-title"
      title={collapsed ? label : undefined}
      sx={{
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        gap: 1.25,
        height: `${SIDE_MENU_ROW_HEIGHT_PX}px`,
        m: 0,
        mx: 0.75,
        px: collapsed ? 0 : 1,
        pb: 0.5,
        boxSizing: "content-box",
        borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.16)}`,
        color: accent
      }}
    >
      <Icon sx={{ fontSize: collapsed ? 22 : 20, flex: "0 0 auto" }} />
      <Typography
        component="span"
        noWrap
        sx={{
          display: collapsed ? "none" : "block",
          fontSize: "15px",
          lineHeight: 1,
          fontWeight: "fontWeightBold",
          color: "inherit"
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
